import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { MESAI_GUNLERI, mesaiMetni, type MesaiAraligi } from "@koclukpanel/contracts";
import { Alan, Buton, Iskelet, Kart, NotKutusu, OnayKutusu } from "@koclukpanel/ui";
import { ApiHatasi, mesaimiGetir, mesaimiYaz } from "./api";

/**
 * MESAİ SAATLERİM — F-MSG-05
 *
 * ================================================================
 * BU SAATLER BİR KISIT DEĞİL, BİR SÖZ
 * ================================================================
 * Şartname: *"Dışında gönderilen mesaj **iletilir** ama gönderene
 * bilgi gösterilir."* Yani mesai dışında yazmak engellenmiyor —
 * engellenseydi gece yarısı bir şey anlatmak isteyen öğrenci onu hiç
 * yazamaz ve sabaha unuturdu. Engellenen tek şey yanlış beklenti.
 *
 * Ekran bunu açıkça yazıyor: koç "saat koyarsam mesaj alamam" diye
 * düşünüp hiç tanımlamazsa, madde hiç çalışmamış olur.
 *
 * ================================================================
 * KİMSE BAŞKASININ SAATİNİ YAZMIYOR
 * ================================================================
 * Uç `@KimlikGerekli()` ile korunuyor: kişi yalnızca kendi kaydını
 * değiştiriyor, ayrı bir izin YOK. Bir izne bağlasaydık, o izni
 * taşımayan bir koç ne zaman cevap verdiğini söyleyemezdi.
 */

/** Gün satırının düzenlenirken tuttuğu hâl. */
interface GunHali {
  acik: boolean;
  bas: string;
  bit: string;
}

const VARSAYILAN: GunHali = { acik: false, bas: "09:00", bit: "18:00" };

/** Sunucudan gelen listeyi gün satırlarına çevirir. */
export function halesiyleAc(araliklar: readonly MesaiAraligi[]): Record<number, GunHali> {
  const hal: Record<number, GunHali> = {};
  for (const [gun] of MESAI_GUNLERI) hal[gun] = { ...VARSAYILAN };
  for (const a of araliklar) {
    /*
      AYNI GÜNDE İKİ ARALIK (öğle arası) sunucuda destekleniyor ama bu
      ekran gün başına TEK aralık çiziyor. İkincisi sessizce KAYBOLMUYOR:
      form yalnızca ilkini gösteriyor ve kaydetmeden hiçbir şey
      değişmiyor — aşağıdaki uyarı bunu söylüyor.
    */
    if (hal[a.day] && !hal[a.day]!.acik) {
      hal[a.day] = { acik: true, bas: a.start, bit: a.end };
    }
  }
  return hal;
}

/** Gün satırlarını sunucunun beklediği listeye çevirir. */
export function listeyeCevir(hal: Record<number, GunHali>): MesaiAraligi[] {
  return MESAI_GUNLERI.filter(([gun]) => hal[gun]?.acik).map(([gun]) => ({
    day: gun,
    start: hal[gun]!.bas,
    end: hal[gun]!.bit,
  }));
}

export function MesaiAyari() {
  const veri = useQuery({ queryKey: ["mesaj-mesai"], queryFn: mesaimiGetir, retry: false });
  const [hal, setHal] = useState<Record<number, GunHali>>(() => halesiyleAc([]));
  const [sonuc, setSonuc] = useState<string | null>(null);

  const araliklar = veri.data?.araliklar;
  useEffect(() => {
    if (araliklar) setHal(halesiyleAc(araliklar));
  }, [araliklar]);

  const kaydet = useMutation({
    mutationFn: () => mesaimiYaz(listeyeCevir(hal)),
    onSuccess: (s) =>
      setSonuc(
        s.araliklar.length === 0
          ? "Mesai saati kaldırıldı. Yazan kişilere artık saat bilgisi gösterilmeyecek."
          : `Kaydedildi: ${mesaiMetni(s.araliklar)}`,
      ),
  });

  /*
    KOÇ DEĞİLSE BÖLÜM HİÇ ÇİZİLMİYOR. Boş bir "mesai saatlerim" kutusu,
    yöneticiye ait olmayan bir ayarı ona aitmiş gibi gösterirdi.
  */
  if (veri.isPending) return <Iskelet satir={3} />;
  if (veri.isError || !veri.data.kocMu) return null;

  const secili = listeyeCevir(hal);

  return (
    <Kart
      baslik="Mesai saatlerim"
      aciklama="Öğrenci ve velilere, mesajınıza ne zaman yanıt geleceğini söyler."
    >
      <div className="flex flex-col gap-3">
        <NotKutusu ton="bilgi">
          <strong>Saat koymak mesaj almanızı engellemez.</strong> Mesai dışında gönderilen mesaj
          size yine ulaşır; yazan kişi yalnızca yanıtın bu saatlerde geleceğini görür. Hiç
          tanımlamazsanız hiçbir saat bilgisi gösterilmez.
        </NotKutusu>

        <ul className="flex flex-col gap-2">
          {MESAI_GUNLERI.map(([gun, ad]) => {
            const g = hal[gun] ?? VARSAYILAN;
            return (
              /*
                TELEFONDA GÜN ÜSTTE, İKİ SAAT ALTTA YAN YANA — UI/UX Dalga 1
                (M-15). `flex-wrap` 390 px'te satırı rastgele kırıyordu:
                bitiş saati alt satıra düşüyor, "–" tek başına kalıyordu ve
                hangi saatin hangi güne ait olduğu okunmuyordu. Izgara
                kırılımı sabitliyor; geniş ekranda tek satır aynen.
              */
              <li key={gun} className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
                <div className="sm:w-32 sm:shrink-0">
                  <OnayKutusu
                    etiket={ad}
                    checked={g.acik}
                    onChange={(e) =>
                      setHal((o) => ({ ...o, [gun]: { ...g, acik: e.target.checked } }))
                    }
                  />
                </div>
                {/*
                  `Alan` KULLANILIYOR, ham `input` DEĞİL: 44px dokunma
                  hedefi ve 16px yazı boyutu oradan geliyor. İkincisi
                  kozmetik değil — iOS Safari 16px altındaki bir alana
                  odaklanınca sayfayı yakınlaştırıp geri
                  uzaklaştırmıyor (`form-bileseni.test.ts`).

                  Etiket GÖRSEL olarak gizli: gün adı zaten solda, onay
                  kutusunun etiketi olarak yazılı. Ama ekran okuyucuda
                  duruyor ve gün adını taşıyor — yoksa kullanıcı on
                  dört tane isimsiz "saat" alanı duyardı.
                */}
                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:flex sm:gap-3">
                  <Alan
                    type="time"
                    etiket={`${ad} başlangıç`}
                    etiketGizli
                    value={g.bas}
                    disabled={!g.acik}
                    onChange={(e) =>
                      setHal((o) => ({ ...o, [gun]: { ...g, bas: e.target.value } }))
                    }
                    className="w-full sm:w-32"
                  />
                  <span className="text-notr-600" aria-hidden="true">
                    –
                  </span>
                  <Alan
                    type="time"
                    etiket={`${ad} bitiş`}
                    etiketGizli
                    value={g.bit}
                    disabled={!g.acik}
                    onChange={(e) =>
                      setHal((o) => ({ ...o, [gun]: { ...g, bit: e.target.value } }))
                    }
                    className="w-full sm:w-32"
                  />
                </div>
              </li>
            );
          })}
        </ul>

        <p className="text-notr-600 text-sm">
          {secili.length === 0
            ? "Hiçbir gün seçilmedi — saat bilgisi gösterilmeyecek."
            : `Görünecek metin: ${mesaiMetni(secili)}`}
        </p>

        <div>
          <Buton
            onClick={() => kaydet.mutate()}
            yukleniyor={kaydet.isPending}
            yukleniyorMetni="Kaydediliyor"
          >
            Kaydet
          </Buton>
        </div>

        {kaydet.isError && (
          <p role="alert" className="text-hata-700 text-sm">
            {kaydet.error instanceof ApiHatasi ? kaydet.error.message : "Kaydedilemedi."}
          </p>
        )}
        {sonuc && (
          <p role="status" className="text-notr-700 text-sm">
            {sonuc}
          </p>
        )}
      </div>
    </Kart>
  );
}
